import type { LanguageCode } from '@sparted/shared-library/business/types';
import { type Dispatch, type SetStateAction, useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { useAppDispatch } from 'Libs/redux/store';
import { CONTENT_STATUS } from 'Store/entities/content/common/constants';
import {
  selectAllDrawerContents,
  selectDrawerContentsMetadata,
} from 'Store/entities/content/content-drawer/content-drawer.selectors';
import { searchDrawerContents } from 'Store/entities/content/content-drawer/content-drawer.thunk';
import { DrawerContent } from 'Store/entities/content/content-drawer/content-drawer.types';

export type UseContentFilters = {
  languageCode: LanguageCode;
  names?: string[];
  thematicIds?: number[];
};

export type UseContents = (args: { limit: number; filters: UseContentFilters }) => {
  offset: number;
  loading: boolean;
  contents: DrawerContent[];
  total: number;
  setOffset: Dispatch<SetStateAction<number>>;
  fetchContents: () => Promise<void>;
};

export const useContents: UseContents = ({ limit, filters }) => {
  const [offset, setOffset] = useState(0);

  const contents = useSelector(selectAllDrawerContents);

  const dispatch = useAppDispatch();

  const metadata = useSelector(selectDrawerContentsMetadata);

  const fetchContents = useCallback(async () => {
    dispatch(
      searchDrawerContents({
        offset,
        limit,
        filters: {
          thematicId: filters.thematicIds && filters.thematicIds.length > 0 ? filters.thematicIds[0] : null,
          languageCodes: filters.languageCode ? [filters.languageCode] : [],
          queries: filters.names ? filters.names : [],
          status: CONTENT_STATUS.VALIDATED,
          archived: false,
          activityIds: [],
          userIds: [],
        },
      }),
    );
  }, [limit, offset, filters]);

  return {
    offset,
    contents,
    loading: metadata.loading,
    total: metadata.total,
    setOffset,
    fetchContents,
  };
};
